import { useDeleteStytchPhoneMutation, useUpdateStytchPhoneMutation } from '@/queries/profile'
import {
	e164ToFormatted,
	formattedToE164,
	formatPhone,
	isValidPhoneFormat,
} from '@/utils/phoneFormatting'
import { useStytchB2BClient, useStytchMember, useStytchOrganization } from '@stytch/react/b2b'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

export function usePhoneManagement() {
	const { member } = useStytchMember()
	const {organization} = useStytchOrganization()
	const stytch = useStytchB2BClient()
	const updatePhoneMutation = useUpdateStytchPhoneMutation()
	const deletePhoneMutation = useDeleteStytchPhoneMutation()

	// Refs for cleanup
	const phoneSuccessTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

	const [tempPhoneNumber, setTempPhoneNumber] = useState(() => {
		// Convert E.164 to formatted display if phone exists
		return e164ToFormatted(member?.mfa_phone_number || '')
	})
	const [phoneSuccess, setPhoneSuccess] = useState(false)
	const [hasBlurred, setHasBlurred] = useState(false)

	// Verification states
	const [isVerificationMode, setIsVerificationMode] = useState(false)
	const [otpCode, setOtpCode] = useState('')
	const [otpSending, setOtpSending] = useState(false)
	const [otpVerifying, setOtpVerifying] = useState(false)

	// Sync temp phone when member data changes
	useEffect(() => {
		if (member?.mfa_phone_number !== undefined) {
			setTempPhoneNumber(e164ToFormatted(member.mfa_phone_number || ''))
		}
	}, [member?.mfa_phone_number])

	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			if (phoneSuccessTimeoutRef.current) {
				clearTimeout(phoneSuccessTimeoutRef.current)
			}
		}
	}, [])

	const validPhone = useMemo(() => {
		// Only validate if field has been blurred
		if (!hasBlurred) {
			return true // Don't show invalid state until blur
		}
		return isValidPhoneFormat(tempPhoneNumber)
	}, [tempPhoneNumber, hasBlurred])

	const handlePhoneChange = (value: string) => {
		const formatted = formatPhone(value)
		setTempPhoneNumber(formatted)
		setHasBlurred(false) // Reset blur state when user starts typing again
	}

	const handlePhoneUpdate = async (newPhone: string) => {
		setHasBlurred(true) // Mark as blurred for validation

		// Convert current member phone to formatted for comparison
		const currentFormatted = e164ToFormatted(member?.mfa_phone_number || '')

		// Check actual phone validity, not the UI validation state
		if (!isValidPhoneFormat(newPhone) || newPhone.trim() === currentFormatted) {
			return
		}

		try {
			// If user already has an MFA phone number (verified or not), delete it first
			if (member?.mfa_phone_number) {
				try {
					await deletePhoneMutation.mutateAsync(undefined)
					toast.success('Previous phone number removed')
				} catch (deleteError) {
					console.error('Failed to delete existing MFA phone number:', deleteError)
					toast.error('Failed to remove previous phone number')
					// Revert UI state on deletion failure
					setTempPhoneNumber(currentFormatted)
					return
				}
			}

			// Convert formatted phone to E.164 format for Stytch
			const e164Phone = formattedToE164(newPhone)

			await updatePhoneMutation.mutateAsync({ mfa_phone_number: e164Phone })

			setPhoneSuccess(true)
			// Clear any existing timeout
			if (phoneSuccessTimeoutRef.current) {
				clearTimeout(phoneSuccessTimeoutRef.current)
			}
			phoneSuccessTimeoutRef.current = setTimeout(() => setPhoneSuccess(false), 2000)
		} catch (error) {
			// Error handling is done in the mutation
			// Revert to original value on error
			setTempPhoneNumber(e164ToFormatted(member?.mfa_phone_number || ''))
		}
	}

	const handlePhoneDelete = async () => {
		if (!member?.mfa_phone_number) {
			return
		}

		try {
			// Use mutation for consistent pattern
			await deletePhoneMutation.mutateAsync(undefined)

			// Clear the temp phone number state
			setTempPhoneNumber('')
			toast.success('Phone number deleted')
		} catch (error) {
			console.error('Failed to delete MFA phone number:', error)
			toast.error('Failed to delete phone number')
		}
	}

	const handleSendOtp = async () => {
		if (!member?.member_id || !organization?.organization_id || !member?.mfa_phone_number) {
			toast.error('Unable to send verification code')
			return
		}

		setOtpSending(true)
		try {
			await stytch.otps.sms.send({
				member_id: member.member_id,
				organization_id: organization.organization_id,
			})
			setIsVerificationMode(true)
			setOtpCode('')
			toast.success('Verification code sent to your phone')
		} catch (error) {
			toast.error('Failed to send verification code')
		} finally {
			setOtpSending(false)
		}
	}

	const handleVerifyOtp = async (code: string) => {
		if (!member?.member_id || !organization?.organization_id || !code.trim()) {
			return
		}

		setOtpVerifying(true)
		try {
			await stytch.otps.sms.authenticate({
				member_id: member.member_id,
				organization_id: organization.organization_id,
				code: code.trim(),
				session_duration_minutes: 60,
			})
			toast.success('Phone number verified successfully!')
			setIsVerificationMode(false)
			setOtpCode('')
		} catch (error) {
			toast.error('Invalid verification code')
		} finally {
			setOtpVerifying(false)
		}
	}

	const handleCancelVerification = () => {
		setIsVerificationMode(false)
		setOtpCode('')
	}

	const handleOtpChange = (value: string) => {
		// Only allow digits and limit to 6 characters
		const digits = value.replace(/\D/g, '').slice(0, 6)
		setOtpCode(digits)
	}

	const hasPhoneNumber = Boolean(member?.mfa_phone_number)
	const canVerify = hasPhoneNumber && !member?.mfa_phone_number_verified

	return {
		// Phone management
		tempPhoneNumber,
		setTempPhoneNumber: handlePhoneChange,
		phoneSuccess,
		validPhone,
		phoneLoading: updatePhoneMutation.isPending || deletePhoneMutation.isPending,
		handlePhoneUpdate,
		handlePhoneDelete,
		hasPhoneNumber,
		memberPhoneNumber: member?.mfa_phone_number || '',
		memberPhoneVerified: member?.mfa_phone_number_verified || false,

		// Verification management
		isVerificationMode,
		otpCode,
		setOtpCode: handleOtpChange,
		otpSending,
		otpVerifying,
		canVerify,
		handleSendOtp,
		handleVerifyOtp,
		handleCancelVerification,
	}
}
